import { Reducer } from "redux";
import { APP_SELECTED_PERMISSIONS_KEY } from "../../constants";
import { IOrgPermission, IUserContext } from "../../types";
import {
  ISavedPermissions,
  ISelectedAppPermissions,
  IUserPermissions,
} from "../auth/authProvider";
import { NO_APP_PERMISSION } from "../constants";
import { localStorageService } from "../util/LocalStorageService";
import { changeUserContext, CHANGE_USER_CONTEXT } from "./actions";

type State = IUserContext;
type Action =
  | ReturnType<typeof changeUserContext>
  | { type: "OTHER_ACTION"; payload?: any };

const GUEST_USER_CONTEXT: IUserContext = {
  isGuest: true,
  isInternalGuest: false,
  isInternal: false,
  isInternalAdmin: false,
  fullname: "Guest",
  email: "guest",
  appPermissions: [NO_APP_PERMISSION],
  orgPermission: undefined,
  initialised: false,
  acceptedTsAndCs: null,
  allAppPermissions: [NO_APP_PERMISSION],
  allOrgPermissions: [],
};

export const userContextReducer: Reducer<State, Action> = (
  previousState = GUEST_USER_CONTEXT,
  action
) => {
  if (action.type === CHANGE_USER_CONTEXT) {
    return action.payload;
  }
  return previousState;
};

const getSelectedAppPermissions = (email: string, appFilter?: string) => {
  const appnameKey = appFilter || "";

  const savedSelections = localStorageService.getItem<ISavedPermissions>(
    `${APP_SELECTED_PERMISSIONS_KEY}.${email}`
  );

  return (
    savedSelections &&
    appnameKey in savedSelections &&
    savedSelections[appnameKey]
  );
};

export const saveSelectedAppPermissions = (
  p: ISelectedAppPermissions,
  email: string,
  appFilter?: string
) => {
  const appnameKey = appFilter || "";

  const savedSelections = localStorageService.getItem<ISavedPermissions>(
    `${APP_SELECTED_PERMISSIONS_KEY}.${email}`
  );

  return localStorageService.setItem<ISavedPermissions>(
    `${APP_SELECTED_PERMISSIONS_KEY}.${email}`,
    { ...savedSelections, [appnameKey]: p }
  );
};

const isAnyInternalUserPermission = (perms?: Array<string>) =>
  perms?.some((p) => p.includes("INTERNAL")) || false;

const isAnyOtherInternalUserPermission = (
  p: IUserPermissions,
  appPerms?: Array<string>
) => {
  if (isAnyInternalUserPermission(appPerms)) {
    return false;
  }
  return p.appPermissions?.some((p) => p.includes("INTERNAL")) || false;
};

const isInternalAdminPermission = (perms?: Array<string>) =>
  perms?.some((p) => p.includes("INTERNAL_ADMIN")) || false;

export const isOrgPermissionMatch = (
  a: IOrgPermission,
  b?: IOrgPermission
): boolean => (b ? JSON.stringify(a) === JSON.stringify(b) : false);

/*
 * Where a user has been configured with multiple permissions that have overlap
 * (e.g. someone is assigned an INTERNAL_VIEWER and an INTERNAL_ADMIN), we need to choose the most appropriate one
 */
const filteredAppPermissionsByPrecedence = (
  permissions: Array<string>,
  appFilter?: string
): Array<string> => {
  const filterPermissions: Array<string> = [];

  const appNames = appFilter
    ? [appFilter]
    : new Set(permissions.map((p) => p.split(".").shift() || ""));

  const has = (permissions: Array<string>, permission: string) =>
    permissions.some((p) => p.toUpperCase() === permission);

  appNames.forEach((appName: string) => {
    if (has(permissions, `${appName}.INTERNAL_ADMIN`)) {
      filterPermissions.push(
        `${appName}.INTERNAL_VIEWER`,
        `${appName}.INTERNAL_EDITOR`,
        `${appName}.EXTERNAL_USER`
      );
    } else if (has(permissions, `${appName}.INTERNAL_EDITOR`)) {
      filterPermissions.push(
        `${appName}.INTERNAL_VIEWER`,
        `${appName}.EXTERNAL_USER`
      );
    } else if (has(permissions, `${appName}.INTERNAL_VIEWER`)) {
      filterPermissions.push(`${appName}.EXTERNAL_USER`);
    }
  });

  return permissions.filter((p) => !filterPermissions.includes(p));
};

export const createUserContext = (
  p: IUserPermissions,
  appFilter?: string,
  defaultAppPermissionSelector: (
    permissions: Array<string>,
    appfilter?: string
  ) => Array<string> = filteredAppPermissionsByPrecedence
): IUserContext => {
  const isGuest = p.fullname === "Guest";
  if (isGuest) {
    return { ...GUEST_USER_CONTEXT, initialised: true };
  }

  const filteredAppPermissions = appFilter
    ? p.appPermissions?.filter((p) => p.startsWith(appFilter))
    : p.appPermissions;

  const filteredOrgPermissions = appFilter
    ? p.orgPermissions?.filter((org) => org.permission?.startsWith(appFilter))
    : p.orgPermissions;

  let appPermissions: Array<string> = [];
  let orgPermission: IOrgPermission | undefined = undefined;

  let selectedAppPermissions = getSelectedAppPermissions(p.email, appFilter);

  if (selectedAppPermissions) {
    appPermissions = selectedAppPermissions.appPermissions;
    orgPermission = selectedAppPermissions.orgPermission;

    /*
     * Check that the saved permissions match the user's current permissions.
     * If they are outdated (e.g. a user was removed from the organisation that
     * previously was their default), then we will create a new default
     */
    if (
      !appPermissions.every((ap) => filteredAppPermissions.includes(ap)) ||
      !filteredOrgPermissions.some((op) =>
        isOrgPermissionMatch(op, orgPermission)
      )
    ) {
      selectedAppPermissions = null;
    }
  }

  if (!selectedAppPermissions) {
    /*
     * If there is no default currently, create one and persist it.
     */
    appPermissions = defaultAppPermissionSelector(
      filteredAppPermissions,
      appFilter
    );

    if (!isAnyInternalUserPermission(appPermissions)) {
      orgPermission = filteredOrgPermissions[0];
    }

    saveSelectedAppPermissions(
      { appPermissions, orgPermission },
      p.email,
      appFilter
    );
  }

  const userContext: IUserContext = {
    isGuest,
    isInternalGuest: isAnyOtherInternalUserPermission(p, appPermissions),
    isInternal: isAnyInternalUserPermission(appPermissions),
    isInternalAdmin: isInternalAdminPermission(appPermissions),
    fullname: p.fullname,
    email: p.email,
    appPermissions /* Implies the 'selected or defaulted app permission', which may be multiple (e.g. you could be an editor with an additional permission for document access*/,
    orgPermission /* Implies the 'selected or defaulted org permission' */,
    initialised: true,
    acceptedTsAndCs: p.acceptedTsAndCs,
    allAppPermissions: filteredAppPermissions /* To provide alternative permissions to select if the app supports it */,
    allOrgPermissions: filteredOrgPermissions /* To provide alternative permissions to select if the app supports it */,
  };
  return userContext;
};
